import React from 'react';
import { Tree, TreeNode } from 'react-organizational-chart';
import styled from 'styled-components';
import { useLanguage } from '@/contexts/LanguageContext';

interface OrgNode {
  id: string;
  name: string | { ar: string; en: string };
  title: string | { ar: string; en: string };
  email: string;
  parentId: string | null;
  order: number;
}

interface OrgChartDisplayProps {
  data: OrgNode[];
}

const StyledNode = styled.div`
  background: linear-gradient(135deg, #7dda55 0%, #6bc444 100%);
  color: white;
  border-radius: 0.5rem;
  padding: 1.25rem 2rem;
  min-width: 240px;
  max-width: 320px;
  width: auto;
  text-align: center;
  box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1);
  transition: all 0.3s ease;
  margin: 0 auto;
  
  &:hover {
    transform: translateY(-4px);
    box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1);
  }
  
  .title {
    font-size: 1.25rem;
    font-weight: bold;
    margin-bottom: 0.5rem;
    color: #004250;
  }
  
  .name {
    font-size: 1.15rem;
    font-weight: 500;
    color: white;
    margin-bottom: 0.25rem;
  }
  
  .email {
    font-size: 0.875rem;
    font-weight: 500;
    color: #004250;
    word-break: break-word;
  }
`;

const StyledTree = styled.div`
  direction: ltr;
  padding-top: 3rem;
  
  /* Hide the line above the root node */
  & > ul > li > ul::before {
    display: none !important;
  }
  
  /* Override library styles for blue lines */
  .rst__lineHalfHorizontalRight::before,
  .rst__lineFullVertical::after,
  .rst__lineHalfVerticalTop::after,
  .rst__lineHalfVerticalBottom::after {
    background-color: #004250 !important;
  }
  
  /* Make connector lines blue */
  & > div > div > div::before,
  & > div > div > div::after {
    background-color: #004250 !important;
  }
  
  /* Hide top connector on first level */
  & > ul > li::before {
    display: none !important;
  }
`;

export function OrgChartDisplay({ data }: OrgChartDisplayProps) {
  const { language } = useLanguage();

  if (!data || data.length === 0) {
    return null;
  }

  const getLocalizedValue = (field: string | { ar: string; en: string }): string => {
    if (typeof field === 'string') return field;
    return field[language] || field.ar || '';
  };

  const buildTree = (parentId: string | null = null): OrgNode[] => {
    return data
      .filter(node => node.parentId === parentId)
      .sort((a, b) => a.order - b.order);
  };

  const renderNode = (node: OrgNode): JSX.Element => {
    const children = buildTree(node.id);

    return (
      <TreeNode
        label={
          <StyledNode>
            <div className="title">{getLocalizedValue(node.title)}</div>
            <div className="name">{getLocalizedValue(node.name)}</div>
            {node.email && <div className="email">{node.email}</div>}
          </StyledNode>
        }
      >
        {children.map(child => renderNode(child))}
      </TreeNode>
    );
  };

  const rootNodes = buildTree(null);

  if (rootNodes.length === 0) {
    return null;
  }

  // If single root, render without wrapper Tree
  if (rootNodes.length === 1) {
    return (
      <StyledTree>
        <Tree
          lineWidth="2px"
          lineColor="#004250"
          lineBorderRadius="10px"
        >
          {renderNode(rootNodes[0])}
        </Tree>
      </StyledTree>
    );
  }

  // Multiple roots
  return (
    <StyledTree>
      <Tree
        lineWidth="2px"
        lineColor="#004250"
        lineBorderRadius="10px"
      >
        {rootNodes.map(node => renderNode(node))}
      </Tree>
    </StyledTree>
  );
}
